import { useEffect, useState } from 'react';
import { FlightOfferStaleError } from '@voll-intelligence/domain';
import { useFlightOffersPort, type FlightLeg } from './flight_offers_port.js';

/**
 * Estado do fetch da linha do tempo por perna do voo. O drawer "Detalhe do voo"
 * abre client-side (sem ida ao agente) e busca lazy as pernas via o port injetado
 * (`getLegs`), keyed no `offerId` opaco da oferta. Sem port (host read-only /
 * preview), fica em erro fail-soft — o drawer mostra só o cabeçalho + aviso.
 * `stale`: a sessão de busca da oferta venceu (409 `offer_stale`, #2332) — a
 * cotação acabou, e nenhum novo fetch a traz de volta.
 */
type FlightLegsState =
  | { phase: 'loading' }
  | { phase: 'ready'; legs: FlightLeg[] }
  | { phase: 'error'; stale: boolean };

/* Antes da resposta ao pedido corrente — constantes de módulo, identidade estável. */
const LOADING: FlightLegsState = { phase: 'loading' };
const NO_PORT: FlightLegsState = { phase: 'error', stale: false };

export function useFlightLegs(offerId: string): FlightLegsState {
  const port = useFlightOffersPort();
  // A resposta carrega o pedido que respondeu: outra oferta (ou outro port) volta a
  // `loading` no mesmo render, sem mostrar as pernas da anterior.
  const [answer, setAnswer] = useState<{
    offerId: string;
    port: typeof port;
    state: FlightLegsState;
  } | null>(null);

  useEffect(() => {
    if (!port) return undefined;
    let cancelled = false;
    const answered = (state: FlightLegsState) => {
      if (!cancelled) setAnswer({ offerId, port, state });
    };
    void port
      .getLegs(offerId)
      .then((legs) => {
        answered({ phase: 'ready', legs });
      })
      .catch((err: unknown) => {
        answered({ phase: 'error', stale: err instanceof FlightOfferStaleError });
      });
    return () => {
      cancelled = true;
    };
  }, [offerId, port]);

  if (!port) return NO_PORT;
  return answer?.offerId === offerId && answer.port === port ? answer.state : LOADING;
}
